/**
 * Knowledge Page - Browse and manage extracted knowledge items
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../platform';
import { getExtractionState, stopExtraction } from '../lib/extraction-state';
import {
  Search,
  Filter,
  Lightbulb,
  BookOpen,
  TrendingUp,
  List,
  MessageSquare,
  Quote,
  GraduationCap,
  FileText,
  Archive,
  Send,
  X,
  Loader2,
  Sparkles,
  ArrowLeft,
} from 'lucide-react';
import { api, type KnowledgeItem } from '../lib/api';

const TYPE_ICONS: Record<string, React.ReactNode> = {
  insight: <Lightbulb className="h-4 w-4" />,
  story: <BookOpen className="h-4 w-4" />,
  metric: <TrendingUp className="h-4 w-4" />,
  framework: <List className="h-4 w-4" />,
  opinion: <MessageSquare className="h-4 w-4" />,
  quote: <Quote className="h-4 w-4" />,
  lesson: <GraduationCap className="h-4 w-4" />,
  anecdote: <FileText className="h-4 w-4" />,
};

const TYPE_COLORS: Record<string, string> = {
  insight: 'bg-yellow-100 text-yellow-800',
  story: 'bg-purple-100 text-purple-800',
  metric: 'bg-green-100 text-green-800',
  framework: 'bg-mazo-100 text-mazo-800',
  opinion: 'bg-pink-100 text-pink-800',
  quote: 'bg-gray-100 text-gray-800',
  lesson: 'bg-orange-100 text-orange-800',
  anecdote: 'bg-indigo-100 text-indigo-800',
};

export function KnowledgePage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Extraction progress state - check persistent storage first
  const [isExtracting, setIsExtracting] = useState(() => {
    const persistentState = getExtractionState();
    return persistentState?.isExtracting ?? searchParams.get('extracting') === 'true';
  });
  const [extractionStartTime] = useState(() => {
    const persistentState = getExtractionState();
    return persistentState?.startTime ?? (isExtracting ? Date.now() : null);
  });
  const previousTotalRef = useRef(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Detail view
  const [selectedItem, setSelectedItem] = useState<KnowledgeItem | null>(null);

  // Available filters
  const [types, setTypes] = useState<Array<{ type: string; count: number }>>([]);
  const [topics, setTopics] = useState<Array<{ topic: string; count: number }>>([]);

  // Selected items for post generation
  const [selectedForPost, setSelectedForPost] = useState<Set<string>>(new Set());
  const [generatingPost, setGeneratingPost] = useState(false);

  // Use refs to track loading state and current filter values
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const initialLoadCalledRef = useRef(false); // Guard against multiple initial loads
  const searchQueryRef = useRef(searchQuery);
  const selectedTypeRef = useRef(selectedType);
  const selectedTopicRef = useRef(selectedTopic);

  // Keep refs in sync with state
  useEffect(() => {
    searchQueryRef.current = searchQuery;
    selectedTypeRef.current = selectedType;
    selectedTopicRef.current = selectedTopic;
  }, [searchQuery, selectedType, selectedTopic]);

  // Load function that reads from refs (stable reference)
  const loadKnowledge = useCallback(async () => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const result = await api.listKnowledge({
        query: searchQueryRef.current || undefined,
        type: selectedTypeRef.current || undefined,
        topic: selectedTopicRef.current || undefined,
        limit: 50,
      });
      if (result.data) {
        setItems(result.data.data);
        setTotal(result.data.total);
      } else if (result.error) {
        setError(result.error.message);
      }
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      hasLoadedRef.current = true;
    }
  }, []); // No dependencies - uses refs

  // Ref to track if filters have been loaded
  const filtersLoadedRef = useRef(false);

  const loadFilters = useCallback(async () => {
    // Prevent loading filters multiple times
    if (filtersLoadedRef.current) return;
    filtersLoadedRef.current = true;

    const [typesResult, topicsResult] = await Promise.all([
      api.getKnowledgeTypes(),
      api.getKnowledgeTopics(),
    ]);

    if (typesResult.data) {
      setTypes(typesResult.data.data);
    }
    if (topicsResult.data) {
      setTopics(topicsResult.data.data);
    }
  }, []);

  // Initial load - only once when authenticated
  // Note: loadKnowledge and loadFilters have stable references (empty deps), so we don't need them in the dependency array
  useEffect(() => {
    // Multiple guards to prevent repeated initial loads
    if (!isAuthenticated) return;
    if (hasLoadedRef.current) return;
    if (initialLoadCalledRef.current) return;

    // Mark as called BEFORE the async operations to prevent race conditions
    initialLoadCalledRef.current = true;

    loadKnowledge();
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Debounced filter changes - triggers on filter state changes only
  // Note: loadKnowledge has a stable reference (empty deps), so we don't need it in the dependency array
  useEffect(() => {
    if (!isAuthenticated || !hasLoadedRef.current) return;

    const timer = setTimeout(() => {
      loadKnowledge();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedType, selectedTopic]);

  // Polling for extraction progress - refresh items while extracting
  useEffect(() => {
    if (!isExtracting || !isAuthenticated) return;

    // Poll every 3 seconds while extracting
    const pollInterval = setInterval(async () => {
      // Reload knowledge items
      const result = await api.listKnowledge({
        query: searchQueryRef.current || undefined,
        type: selectedTypeRef.current || undefined,
        topic: selectedTopicRef.current || undefined,
        limit: 50,
      });

      if (result.data) {
        const newTotal = result.data.total;
        setItems(result.data.data);
        setTotal(newTotal);

        // If no new items for 30 seconds after we started getting items, assume extraction is done
        if (newTotal > 0 && newTotal === previousTotalRef.current) {
          const elapsedMs = Date.now() - (extractionStartTime || Date.now());
          // Wait at least 30 seconds before declaring done (to handle slow extraction)
          if (elapsedMs > 30000) {
            setIsExtracting(false);
            // Clear persistent extraction state
            stopExtraction();
            // Remove the extracting param from URL
            setSearchParams({});
          }
        }
        previousTotalRef.current = newTotal;
      }

      // Also refresh filters
      const [typesResult, topicsResult] = await Promise.all([
        api.getKnowledgeTypes(),
        api.getKnowledgeTopics(),
      ]);
      if (typesResult.data) setTypes(typesResult.data.data);
      if (topicsResult.data) setTopics(topicsResult.data.data);
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [isExtracting, isAuthenticated, extractionStartTime, setSearchParams]);

  const handleArchive = async (id: string) => {
    const result = await api.archiveKnowledge(id);
    if (result.data) {
      setItems(items.filter(i => i.id !== id));
      setSelectedItem(null);
    } else if (result.error) {
      setError(result.error.message);
    }
  };

  const toggleSelectForPost = (id: string) => {
    const newSelected = new Set(selectedForPost);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedForPost(newSelected);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedType('');
    setSelectedTopic('');
  };

  const handleGeneratePost = useCallback(async () => {
    if (selectedForPost.size === 0) {
      setError('Please select at least one knowledge item');
      return;
    }

    console.log('Setting generatingPost to true');
    setGeneratingPost(true);
    setError(null);

    // Small delay to ensure the banner renders before the API call
    await new Promise(resolve => setTimeout(resolve, 100));
    console.log('Starting post generation API call');

    try {
      const result = await api.generatePost({
        knowledge_item_ids: Array.from(selectedForPost),
        length_preset: 'medium',
        tone_preset: 'pragmatic',
      });

      if (result.error) {
        setError(result.error.message);
        setGeneratingPost(false);
        return;
      }

      // Get the new post ID from the response
      const newPost = result.data?.data;
      if (!newPost || !newPost.id) {
        setError('Post generated but ID not returned');
        setGeneratingPost(false);
        return;
      }

      // Clear selection
      setSelectedForPost(new Set());
      setGeneratingPost(false);

      // Navigate to posts page with the new post selected
      navigate(`/posts?post=${newPost.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate post');
      setGeneratingPost(false);
    }
  }, [selectedForPost, navigate]);

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Please sign in to view knowledge.</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)]">
      {/* Main list */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Post Generation Progress Banner */}
        {generatingPost && (
          <div className="sticky top-0 z-50 bg-gradient-to-r from-mazo-50 to-mazo-100 border-b-2 border-mazo-300 px-6 py-5 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-mazo-200 flex-shrink-0">
                <Loader2 className="h-6 w-6 text-mazo-700 animate-spin" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="h-5 w-5 text-mazo-700" />
                  <span className="font-bold text-mazo-900 text-xl">Generating your post...</span>
                </div>
                <p className="text-base text-mazo-800 font-medium">
                  Creating a LinkedIn post from {selectedForPost.size} knowledge item{selectedForPost.size > 1 ? 's' : ''}.
                  You'll be redirected to the Posts page in a moment.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Extraction Progress Banner */}
        {isExtracting && (
          <div className="bg-gradient-to-r from-mazo-50 to-mazo-100 border-b border-mazo-200 px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-mazo-100">
                <Loader2 className="h-4 w-4 text-mazo-500 animate-spin" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-mazo-500" />
                  <span className="font-medium text-mazo-900">Extracting knowledge from your sources...</span>
                </div>
                <p className="text-sm text-mazo-700 mt-0.5">
                  {total > 0
                    ? `${total} knowledge items extracted so far. New items will appear automatically.`
                    : 'Processing your documents. This may take a few minutes.'}
                </p>
              </div>
              <button
                onClick={() => {
                  setIsExtracting(false);
                  stopExtraction();
                  setSearchParams({});
                }}
                className="text-mazo-500 hover:text-mazo-700 text-sm underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Header with back button */}
        <div className="p-4 border-b bg-white flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Sources
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Knowledge Base</h1>
            <p className="text-sm text-gray-500">{total} items extracted from your sources</p>
          </div>
        </div>

        {/* Search and filters */}
        <div className={`p-4 border-b bg-white space-y-4 ${generatingPost ? 'opacity-60 pointer-events-none' : ''}`}>
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                disabled={generatingPost}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search knowledge..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                showFilters ? 'bg-mazo-50 border-mazo-200 text-mazo-700' : 'hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {(selectedType || selectedTopic) && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-mazo-400 text-white rounded-full">
                  {(selectedType ? 1 : 0) + (selectedTopic ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* Filter dropdowns */}
          {showFilters && (
            <div className="flex items-center gap-4">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400"
              >
                <option value="">All Types</option>
                {types.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.type.charAt(0).toUpperCase() + t.type.slice(1)} ({t.count})
                  </option>
                ))}
              </select>

              <select
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400"
              >
                <option value="">All Topics</option>
                {topics.slice(0, 20).map((t) => (
                  <option key={t.topic} value={t.topic}>
                    {t.topic} ({t.count})
                  </option>
                ))}
              </select>

              {(selectedType || selectedTopic || searchQuery) && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Stats bar */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>{total} knowledge items</span>
            {selectedForPost.size > 0 && (
              <button
                type="button"
                onClick={() => {
                  console.log('Button clicked!');
                  handleGeneratePost();
                }}
                disabled={generatingPost}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-mazo-500 text-white rounded-lg hover:bg-mazo-400 disabled:opacity-50"
              >
                {generatingPost ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Post from {selectedForPost.size} item{selectedForPost.size > 1 ? 's' : ''}
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className={`flex-1 overflow-y-auto relative ${generatingPost ? 'opacity-60 pointer-events-none' : ''}`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-mazo-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Lightbulb className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No knowledge yet</h3>
              <p className="text-gray-500 max-w-md">
                Add sources and sync them to extract knowledge items automatically.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedItem?.id === item.id ? 'bg-mazo-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedForPost.has(item.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelectForPost(item.id);
                      }}
                      className="mt-1 h-4 w-4 text-mazo-400 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[item.type]}`}>
                          {TYPE_ICONS[item.type]}
                          {item.type}
                        </span>
                        {item.confidence && (
                          <span className="text-xs text-gray-400">
                            {Math.round(item.confidence * 100)}% confidence
                          </span>
                        )}
                      </div>
                      <h3 className="font-medium text-gray-900 truncate">{item.title}</h3>
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">{item.summary}</p>
                      {item.topics && item.topics.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.topics.slice(0, 3).map((topic) => (
                            <span
                              key={topic}
                              className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              {topic}
                            </span>
                          ))}
                          {item.topics.length > 3 && (
                            <span className="text-xs text-gray-400">
                              +{item.topics.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedItem && (
        <div className="w-96 border-l bg-white overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Details</h2>
            <button
              onClick={() => setSelectedItem(null)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            {/* Type badge */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${TYPE_COLORS[selectedItem.type]}`}>
              {TYPE_ICONS[selectedItem.type]}
              <span className="font-medium capitalize">{selectedItem.type}</span>
            </div>

            {/* Title */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Title
              </label>
              <p className="text-gray-900 font-medium mt-1">{selectedItem.title}</p>
            </div>

            {/* Summary */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Summary
              </label>
              <p className="text-gray-700 mt-1">{selectedItem.summary}</p>
            </div>

            {/* Details */}
            {selectedItem.details && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Details
                </label>
                <p className="text-gray-700 mt-1 whitespace-pre-wrap">{selectedItem.details}</p>
              </div>
            )}

            {/* Provenance */}
            {selectedItem.snippet && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Source Snippet
                </label>
                <blockquote className="mt-1 pl-3 border-l-2 border-gray-200 text-sm text-gray-600 italic">
                  "{selectedItem.snippet}"
                </blockquote>
              </div>
            )}

            {/* Topics */}
            {selectedItem.topics && selectedItem.topics.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Topics
                </label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedItem.topics.map((topic) => (
                    <span
                      key={topic}
                      onClick={() => setSelectedTopic(topic)}
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm cursor-pointer hover:bg-gray-200"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Confidence */}
            {selectedItem.confidence !== undefined && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Confidence
                </label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-mazo-500 rounded-full"
                      style={{ width: `${selectedItem.confidence * 100}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600">
                    {Math.round(selectedItem.confidence * 100)}%
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <button
                onClick={() => {
                  toggleSelectForPost(selectedItem.id);
                }}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  selectedForPost.has(selectedItem.id)
                    ? 'bg-mazo-100 text-mazo-700'
                    : 'bg-mazo-500 text-white hover:bg-mazo-400'
                }`}
              >
                <Send className="h-4 w-4" />
                {selectedForPost.has(selectedItem.id) ? 'Remove from Post' : 'Use in Post'}
              </button>
              <button
                onClick={() => handleArchive(selectedItem.id)}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Archive"
              >
                <Archive className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 shadow-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
